import React from 'react';
import { ArrowRight, Check, CircleDot } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import WorkflowSwitcher from '../components/WorkflowSwitcher';
import { ClosingCta, PrimaryCta, Reveal, SectionIntro } from '../components/Alt4Shared';

const journey = ['Access', 'Prepare', 'Treat', 'Claim', 'Collect', 'Continue'];

export default function Alt4Workflows() {
  return (
    <Alt4Layout title="Workflows">
        <section className="alt4-page-hero alt4-section">
          <div className="alt4-container alt4-hero-grid alt4-page-hero__grid">
            <Reveal className="alt4-hero-copy">
              <span className="alt4-eyebrow">Connected workflows</span>
              <h1>The patient journey, completed end to end.</h1>
              <p>Asha carries context and action across the patient journey, so work does not stall at the boundary between departments or tools.</p>
              <PrimaryCta />
            </Reveal>
            <Reveal className="alt4-panel alt4-journey-map" delay={0.08}>
              <header><span className="alt4-interface-label">Patient journey · In motion</span><span className="alt4-status alt4-status--running"><CircleDot aria-hidden="true" /> Active</span></header>
              <div className="alt4-journey-map__stages">
                {journey.map((stage, index) => (
                  <React.Fragment key={stage}>
                    <div className={index < 3 ? 'is-complete' : index === 3 ? 'is-active' : ''}>
                      <span>{index < 3 ? <Check aria-hidden="true" /> : index + 1}</span>
                      <strong>{stage}</strong>
                    </div>
                    {index < journey.length - 1 && <ArrowRight aria-hidden="true" />}
                  </React.Fragment>
                ))}
              </div>
              <div className="alt4-journey-map__context">
                <span>Context moving with the work</span>
                <p>Patient state, office rules, prior actions, and open exceptions remain connected.</p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="alt4-section alt4-section--pale">
          <div className="alt4-container">
            <SectionIntro
              eyebrow="Explore the operating flows"
              title="One journey. Five coordinated workflow groups."
              body="Select a workflow to see the trigger, shared context, actions, exception path, and completed state as one connected run."
            />
            <WorkflowSwitcher />
          </div>
        </section>

        <section className="alt4-section">
          <div className="alt4-container alt4-grid alt4-grid--two alt4-workflow-continuity">
            <SectionIntro
              eyebrow="No dropped context"
              title="The next workflow starts with what the last one learned."
              body="A completed preparation task updates the visit state. The visit informs the claim. The claim informs follow-through. Every transition keeps the patient and the office on one timeline."
            />
            <div className="alt4-panel alt4-handoff-panel">
              <span className="alt4-interface-label">Connected handoff</span>
              <div><span className="alt4-status alt4-status--done">Done</span><strong>Visit readiness</strong><small>Context and actions recorded</small></div>
              <ArrowRight aria-hidden="true" />
              <div><span className="alt4-status alt4-status--running">Running</span><strong>Clinical to claim</strong><small>Starting with the complete visit state</small></div>
              <ArrowRight aria-hidden="true" />
              <div><span className="alt4-status">Queued</span><strong>Revenue follow-through</strong><small>Waiting on the verified outcome</small></div>
            </div>
          </div>
        </section>

        <ClosingCta
          eyebrow="Follow the work"
          title="See the patient journey run as one connected operation."
          body="Walk through the workflow groups and the shared context that carries each task forward."
        />
    </Alt4Layout>
  );
}
